import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  // Iter-329 — smart account-discovery guidance (from /auth/forgot-password response)
  const [accountHint, setAccountHint] = useState(null);

  // Iter-225j — pre-fill email + show a recovery banner when the user
  // arrives here from a trapped-login redirect (account without a
  // password_hash). Keeps the "set your password" journey single-flow.
  const reason = searchParams.get('reason');

  useEffect(() => {
    const e = searchParams.get('email');
    if (e) setEmail(e);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setAccountHint(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok) {
        // Iter-329 — response now includes account_status hints for dormant/import/invite states
        setAccountHint({ status: data.account_status, next: data.next_action, message: data.message });
        setSent(true);
        toast.success('Reset link sent!');
      } else {
        toast.error(data.detail || 'Something went wrong');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center auth-background" data-testid="forgot-password-page">
      <Card className="w-full max-w-md glassmorphism border-zinc-800">
        <CardHeader className="space-y-3 text-center">
          <div className="flex justify-center mb-2">
            <img
              src="https://res.cloudinary.com/dzlc6qta0/image/upload/v1771451035/d2e1bd_3ffc4a4bc88e4587b369a584fd8a636b_mv2_pvlple.png"
              alt="iEvntür Logo"
              className="h-12"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            {sent ? 'Check your email' : (reason === 'password_not_set' ? 'Set your password' : 'Reset your password')}
          </CardTitle>
          <CardDescription className="text-zinc-400">
            {sent
              ? 'We sent a reset link to your email. It expires in 24 hours.'
              : (reason === 'password_not_set'
                  ? "Your account doesn't have a password yet. Enter your email and we'll send you a secure link to set one."
                  : 'Enter the email address associated with your account')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!sent && reason === 'password_not_set' && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-amber-300 text-sm" data-testid="trapped-account-banner">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Approved account detected with no password on file. This is a one-time setup — your account stays intact.</span>
            </div>
          )}
          {sent ? (
            <div className="space-y-4 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-zinc-400 text-sm">
                Didn't receive it? Check your spam folder or{' '}
                <button onClick={() => setSent(false)} className="text-cyan-500 hover:text-cyan-400 underline">try again</button>.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full mt-4 border-zinc-700 text-white hover:bg-zinc-800" data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-white">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                    className="h-11 pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-cyan-500 hover:bg-cyan-600 text-black font-medium"
                disabled={loading}
                data-testid="send-reset-btn"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <Link to="/login" className="block text-center text-sm text-zinc-400 hover:text-white transition-colors" data-testid="back-link">
                <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Back to Sign In
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
